import {
    Layout,
    Button,
    Page,
    Toast,
    Form,
    FormLayout,
    TextField,
    LegacyCard,
    ResourceList,
    ResourceItem,
    Text, Frame
} from '@shopify/polaris';

import {useState, useEffect, useCallback, useMemo} from 'react';
import {useLocation, useHistory} from 'react-router-dom';
import Api from "../../helpers/api";
import Paginator from "../../components/Paginator";
import {getErrorMessage} from "../../helpers/utils";

function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
}

const Users = () => {
    const queryString = useQuery();
    const history = useHistory();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [errors, setErrors] = useState(null);
    const [loading, setLoading] = useState(false);
    const [toast, setToast] = useState({
        active: false,
        message: ''
    });
    const [meta, setMeta] = useState({currentPage: 1, lastPage: 1})

    const toastMarkup = toast.active ? (
        <Toast content={toast.message} duration={3000} onDismiss={() => { setToast({ message: '', active: false }) }} />
    ) : null;

    const assignUser = (user) => history.push(`/users/${user.id}`);

    useEffect(() => {
        if (queryString.get('q') == '' || queryString.get('q') == null) {
            return;
        }

        setQuery(queryString.get('q'));
        loadUsers(queryString.get('q'));
    }, []);

    const handleSubmit = () => {
        if (query == '') {
            setErrors('Please type something in the search field.')
            return;
        };

        history.push('/users?' + (new URLSearchParams({q: query})));
        loadUsers(query, 1, true);
    }

    const loadUsers = (q = null, page = 1, canRedirect = false) => {
        setErrors(null);
        setLoading(true);

        Api
            .findUsers({
                query: q ?? query,
                page: page
            })
            .then(({data: result}) => {
                if (result.data.length === 1 && canRedirect) {
                    assignUser(result.data[0]);
                } else if (result.data.length === 0) {
                    setToast({active: true, 'message': 'There are no results matching your search.'});
                    setResults([]);
                } else {
                    setResults(result.data);
                }
                setMeta({currentPage: result.current_page, lastPage: result.last_page});
            })
            .catch(err => {
                // 403 is already reported by the API client.
                if (err.response?.status !== 403) {
                    setToast({active: true, message: getErrorMessage(err)})
                }
            })
            .finally(() => setLoading(false));
    };

    return (
        <Frame>
            <Page title="Users"
                backAction={{ content: 'Go back', onAction: () => history.goBack() }}
                >
                <Layout>
                    <Layout.Section>
                        <Form onSubmit={handleSubmit}>
                            <FormLayout>
                                <TextField
                                    value={query}
                                    onChange={setQuery}
                                    label="Search users by email or name"
                                    type="text"
                                    error={errors}
                                    connectedRight={<Button submit  variant="primary">Search</Button>}
                                />
                            </FormLayout>
                        </Form>
                    </Layout.Section>

                    {results.length > 0 && (
                        <Layout.Section>
                            <Paginator currentPage={meta.currentPage} lastPage={meta.lastPage} show={true} callback={(page) => loadUsers(null, page)} />
                            <LegacyCard title="Search Results">
                                <ResourceList
                                    loading={loading}
                                    resourceName={{ singular: 'user', plural: 'users' }}
                                    items={results}
                                    renderItem={(item) => {
                                        return (
                                            <ResourceItem
                                                id={item.id}
                                                onClick={event => { assignUser(item) }}
                                                accessibilityLabel={`View details for user ${item.email}`}
                                            >
                                                <h3>
                                                    <Text as="span" fontWeight="semibold">{item.name}</Text>
                                                </h3>
                                                <div>{`Email: ${item.email}`}</div>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                            </LegacyCard>
                            <Paginator currentPage={meta.currentPage} lastPage={meta.lastPage} show={true} callback={(page) => loadUsers(null, page)} />
                        </Layout.Section>
                    )}
                </Layout>
                {toastMarkup}
            </Page></Frame>
    );
}

export default Users;
